import axios from 'axios';
import QRCode from 'qrcode';
import * as config from '@/config';

const api = config.apiUrl;

/**
 * Fetches QR code data (EPC string) from the API for a given order ID
 * @param {number} orderId - The order ID to fetch QR code data for
 * @returns {Promise<string>} The EPC string from the API
 */
export const getQRCodeData = async (orderId) => {
    try {
        const response = await axios.get(`${api}/qr-code/${orderId}?t=${new Date().getTime()}`);
        
        if (!response.data.qrCodeData) {
            throw new Error(`No QR code data found for order ID: ${orderId}`);
        }

        return response.data.qrCodeData;
    } catch (error) {
        console.error('Error fetching QR code data:', error);
        throw error;
    }
};

/**
 * Generates a QR code data URL from an EPC string
 * @param {string} epcString - The EPC string to encode in the QR code
 * @returns {Promise<string>} Data URL of the generated QR code image
 */
export const generateQRCode = async (epcString) => {
    try {
        const qrOptions = {
            type: 'image/png',
            quality: 0.92,
            margin: 1,
            width: 512,
            errorCorrectionLevel: 'M'
        };

        const dataUrl = await QRCode.toDataURL(epcString, qrOptions);
        return dataUrl;
    } catch (error) {
        console.error('Error generating QR code:', error);
        throw error;
    }
};

/**
 * Fetches QR code data and generates QR code in one step
 * @param {number} orderId - The order ID to generate QR code for
 * @returns {Promise<{epcString: string, qrCodeDataUrl: string}>} Object containing EPC string and QR code data URL
 */
export const getQRCodeForOrder = async (orderId) => {
    try {
        const epcString = await getQRCodeData(orderId);
        const qrCodeDataUrl = await generateQRCode(epcString);
        
        return {
            epcString,
            qrCodeDataUrl
        };
    } catch (error) {
        console.error('Error getting QR code for order:', error);
        throw error;
    }
}; 